/**
 * Batch H2 — Holder claim detail (/buyer/claims/:redemptionId).
 * Claim (link used) → Entitlement (saved on account) → NFT issuance (separate) → Benefits.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Gift, HelpCircle } from 'lucide-react';
import theme from '../theme';
import { ClaimLifecycle } from '../components/ClaimLifecycle';
import BenefitList from '../components/holder/BenefitList';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const card = { background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card, padding: 20, marginBottom: 16 };
const Row = ({ label, value, testid }) => (
  <div style={{ display: 'flex', gap: 12, padding: '6px 0', borderTop: `1px solid ${theme.colors.borders}`, fontSize: 13 }}>
    <div style={{ width: 170, color: theme.colors.text.muted, flexShrink: 0 }}>{label}</div>
    <div data-testid={testid} style={{ color: theme.colors.text.primary, wordBreak: 'break-all' }}>{value ?? '—'}</div>
  </div>
);

export default function BuyerClaimDetailPage() {
  const { redemptionId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);

  const load = useCallback(async () => {
    try { setData((await axios.get(`${API}/buyer/claims/${redemptionId}`, auth)).data); }
    catch (e) { setErr(e?.response?.data?.detail || 'Could not load this claim'); }
  }, [redemptionId, auth]);
  useEffect(() => { load(); }, [load]);

  const back = (
    <button data-testid="claim-detail-back-btn" onClick={() => navigate('/buyer/claims')}
      style={{ display: 'inline-flex', gap: 6, alignItems: 'center', background: 'none', border: 'none', color: theme.colors.primary, fontWeight: 700, cursor: 'pointer', marginBottom: 12 }}>
      <ArrowLeft size={14} /> Back to My Claims
    </button>
  );
  if (err) return (
    <div style={{ padding: '24px 28px', maxWidth: 900, margin: '0 auto' }}>{back}
      <div data-testid="claim-detail-error" style={{ ...card, color: '#991B1B' }}>{err}. It may belong to a different account.</div>
    </div>
  );
  if (!data) return <div data-testid="claim-detail-loading" style={{ padding: 28 }}>Loading claim…</div>;

  const { claim, portfolio, entitlement, issuance, nft_contract: nc, benefits, next_actions } = data;
  const iss = issuance || {};
  return (
    <div data-testid="claim-detail-page" style={{ padding: '24px 28px', maxWidth: 900, margin: '0 auto' }}>
      {back}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 4 }}>
        <Gift size={22} color={theme.colors.primary} />
        <h1 data-testid="claim-detail-title" style={{ fontSize: 24, fontWeight: 900, color: theme.colors.text.primary }}>{claim.title || 'Kickback claim'}</h1>
      </div>
      <div style={{ fontSize: 13, color: theme.colors.text.muted, marginBottom: 16 }}>
        From <b>{data.creator_name}</b> · Kickback <b data-testid="claim-detail-portfolio">{portfolio.name}</b> ({portfolio.status})
      </div>

      <div style={card}>
        <ClaimLifecycle claimedAt={claim.claimed_at} entitlementStatus={entitlement?.status} issuance={issuance} testidPrefix="claim-detail" />
      </div>

      <div style={card}>
        <div style={{ fontWeight: 800, marginBottom: 6 }}>Details</div>
        <Row label="Claim type" value={claim.claim_type} testid="claim-detail-type" />
        <Row label="Claim ID" value={claim.redemption_id} testid="claim-detail-id" />
        <Row label="Claimed" value={new Date(claim.claimed_at).toLocaleString()} />
        <Row label="Entitlement" value={`${entitlement?.status} · ${entitlement?.id}`} testid="claim-detail-entitlement" />
        <Row label="Is an NFT required?" value={data.issuance_required} />
        <Row label="NFT issuance" value={iss.label} testid="claim-detail-issuance" />
        {iss.status === 'issued' && <Row label="Token" value={`#${iss.token_id} · ${iss.contract_address}`} />}
        {iss.status === 'issued' && iss.tx_hash && <Row label="Transaction" value={iss.tx_hash} />}
        <Row label="Kickback NFT contract" testid="claim-detail-contract"
          value={nc ? `${nc.chain_label} · ${nc.standard} · ${nc.contract_address}` : 'No deployed NFT contract yet'} />
      </div>

      <div style={card}>
        <div style={{ fontWeight: 800, marginBottom: 4 }}>Benefits</div>
        <div style={{ fontSize: 12, color: theme.colors.text.muted, marginBottom: 10 }}>
          Each benefit has its own unlock rules set by the creator. Unlocking makes it available; redeeming records that you've used it.
        </div>
        <BenefitList benefits={benefits} portfolioId={portfolio.id} onRedeemed={load} />
      </div>

      <div style={card} data-testid="claim-detail-next">
        <div style={{ fontWeight: 800, marginBottom: 6 }}>What you can do next</div>
        {next_actions.length === 0 && <div style={{ fontSize: 13, color: theme.colors.text.muted }}>Nothing needed right now. We'll show new benefits here when they unlock.</div>}
        {next_actions.map(a => (
          <div key={a.code} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13, padding: '4px 0' }}>
            <span style={{ flex: 1 }}>{a.label}</span>
            {a.route && <button data-testid={`claim-detail-action-${a.code}`} onClick={() => navigate(a.route)}
              style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${theme.colors.primary}`, background: '#fff', color: theme.colors.primary, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>Open</button>}
          </div>
        ))}
        <div style={{ marginTop: 10, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button data-testid="claim-detail-help-btn" onClick={() => navigate('/buyer/help')} style={{ display: 'inline-flex', gap: 6, alignItems: 'center', background: 'none', border: 'none', color: theme.colors.primary, cursor: 'pointer', fontSize: 13 }}>
            <HelpCircle size={14} /> Get help with this claim
          </button>
          <button data-testid="claim-detail-dashboard-btn" onClick={() => navigate('/buyer/dashboard')} style={{ background: 'none', border: 'none', color: theme.colors.primary, cursor: 'pointer', fontSize: 13 }}>
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
